import React, { useState } from 'react';

import isEqual from 'lodash/isEqual';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Chip, Stack, type SxProps, type Theme, Typography } from '@mui/material';

interface Props {
  previous: Record<string, unknown> | null;
  proposed: Record<string, unknown>;
  compact?: boolean;
}

export function getChangedFields(
  previous: Record<string, unknown> | null,
  proposed: Record<string, unknown>,
): Array<{ key: string; prev: unknown; next: unknown }> {
  const allKeys = Array.from(new Set([...Object.keys(previous ?? {}), ...Object.keys(proposed)]));
  return allKeys
    .map((key) => ({ key, prev: previous?.[key], next: proposed[key] }))
    .filter(({ prev, next }) => !isEqual(prev, next));
}

const VALUE_TRUNCATE_LENGTH = 200;

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function TruncatedValue({ value, label, sx }: { value: unknown; label?: string; sx: SxProps<Theme> }) {
  const [expanded, setExpanded] = useState(false);
  const rendered = renderValue(value);
  const isTruncated = rendered.length > VALUE_TRUNCATE_LENGTH;
  const display = !expanded && isTruncated ? rendered.slice(0, VALUE_TRUNCATE_LENGTH) + '...' : rendered;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {label && (
        <Typography
          component="span"
          sx={{ fontSize: 10, color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1 }}
        >
          {label}
        </Typography>
      )}
      <Chip
        size="small"
        label={display}
        sx={{
          ...sx,
          fontFamily: 'monospace',
          fontSize: 11,
          maxWidth: 300,
          height: 'auto',
          '& .MuiChip-label': { whiteSpace: 'pre-wrap', wordBreak: 'break-all' },
        }}
      />
      {isTruncated && (
        <Button size="small" sx={{ fontSize: 10, p: 0, ml: 0.5 }} onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Box>
  );
}

export default function ValueDiff({ previous, proposed, compact }: Props) {
  const changed = getChangedFields(previous, proposed);

  if (changed.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No field changes detected
      </Typography>
    );
  }

  if (compact) {
    return (
      <>
        {changed.map(({ key, prev, next }) => (
          <Box key={key} sx={{ mb: 0.5, wordBreak: 'break-all' }}>
            <Box component="span" sx={{ color: 'text.secondary', fontSize: 11 }}>
              {key}:
            </Box>{' '}
            {prev !== undefined && prev !== null && (
              <Box component="span" sx={{ color: 'error.main' }}>
                − {JSON.stringify(prev)}{' '}
              </Box>
            )}
            {next !== undefined && (
              <Box component="span" sx={{ color: 'success.main' }}>
                + {JSON.stringify(next)}
              </Box>
            )}
          </Box>
        ))}
      </>
    );
  }

  return (
    <Stack spacing={0.5}>
      {changed.map(({ key }) => {
        const prevVal = previous?.[key];
        const newVal = proposed[key];

        return (
          <Box key={key}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {key}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {previous !== null && (
                <TruncatedValue
                  label="Old"
                  value={prevVal}
                  sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}
                />
              )}
              <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <TruncatedValue
                label="New"
                value={newVal}
                sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
              />
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
